import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * The show editor's footer ribbon is PINNED chrome (`ShowEditorModal`), but the control that belongs in it —
 * the active section's primary Save — is owned by the section, which holds the form state, the mutation and
 * the `If-Match` version. This is the seam: the modal publishes two DOM hosts, the mounted section portals
 * its Save (and, on 09 Financials, the `Save agent / manager % as defaults` link) into them.
 *
 * Why a portal rather than a callback registry: a Save button's identity changes on every keystroke (it
 * closes over the form), so re-registering it as state would loop. A portal moves the SAME element and
 * re-renders it in place, and it keeps React context (band, query client) flowing from the section.
 *
 * With no host — `ShowWizard` mounted standalone, as its own unit tests do — the children render INLINE
 * where they are written, so the wizard never loses its Save.
 */
export interface EditorFooterHosts {
  /** The trailing action cluster, after Delete / Confirm hold. */
  save: HTMLElement | null;
  /** The leading slot, for a section-scoped link (the agent/manager-% defaults star). */
  extra: HTMLElement | null;
}

const EditorFooterContext = createContext<EditorFooterHosts | null>(null);

export function EditorFooterProvider({ hosts, children }: { hosts: EditorFooterHosts; children: ReactNode }) {
  return <EditorFooterContext.Provider value={hosts}>{children}</EditorFooterContext.Provider>;
}

export function EditorFooterSlot({
  zone = "save",
  children,
}: {
  zone?: keyof EditorFooterHosts;
  children: ReactNode;
}) {
  const hosts = useContext(EditorFooterContext);
  // The `save` zone no longer portals. Under the single scroll all ten sections are mounted at once, so
  // portaling each one's Save put TEN buttons in one ribbon. A section's Save now renders where the section
  // is — which is also where you are reading — and the ribbon carries ONE Save that commits everything dirty
  // (`EditorSaveRegistry`). The `extra` zone still portals: the Financials `★ save % as defaults` link is
  // genuinely footer chrome and there is only ever one of it.
  const host = zone === "extra" ? (hosts?.extra ?? null) : null;
  return host ? createPortal(children, host) : <>{children}</>;
}
